<template>
    <div id="telephone-toolbar">
        <div v-if="screenMdDown">
            <action-menu v-if="hasAbsencesModule" wrapper-element="div" style="float: none;">
                <template #button="{toggle}">
                    <div @click="toggle">
                        <i class="material-icons md-18" aria-hidden="true" role="img">phone</i> <span>Telefonliste</span>
                    </div>
                </template>

                <li v-for="(status, key) in telephoneToolbarList" @click="openList(key)" class="show-colors">
                    <a href="javascript: void(0);">
                        <i class="material-icons" aria-hidden="true" :style="{color: status.color}" :class="status.class" role="img">{{status.icon}}</i>
                        <span class="default-text-color">{{status.title}}</span>
                        <span class="text-grey">&nbsp;({{status.count}})</span>
                    </a>
                </li>
            </action-menu>
            <div v-else class="toolbar-button" @click="openList(null)">
                <i class="material-icons md-18" aria-hidden="true" role="img">phone</i> <span>Telefonliste</span>
            </div>
        </div>
        <template v-else>
            <span>
                <span v-interact="telephoneToolbarToggle" class="icon-interact">
                    <i aria-hidden="true" class="material-icons" role="img">chevron_{{visibility ? 'right' : 'left'}}</i>
                </span>
            </span>

            <template v-if="visibility">
                <span class="pointer" @click="openList(null)"><i class="material-icons" aria-hidden="true" role="img">phone</i> Telefonliste</span>
                <template v-if="hasAbsencesModule">
                    <span class="pointer" v-for="(status, key) in telephoneToolbarList" @click="openList(key)">
                        <i class="material-icons" aria-hidden="true" :style="{color: status.color}" :class="status.class" role="img">{{status.icon}}</i> {{status.title}} ({{status.count}})
                    </span>
                </template>
                <span v-if="telephoneToolbar.openRequestCount" class="pointer" v-interact="() => openOpenRequestPopup()">
                        <i class="material-icons" aria-hidden="true" role="img">access_time</i> Offene Anträge ({{telephoneToolbar.openRequestCount}})
                    </span>
            </template>
        </template>

        <app-popup ref="telephonePopup" v-model="isOpen" title="Telefonliste" title-icon="phone" @close="closePopup">
            <template #default>
                <app-search-input v-model="filter.search" autofocus/>

                <table class="portuna-table portuna-table-sm-padding portuna-table-bordered">
                    <colgroup>
                        <col style="width: auto;"/>

                        <col style="width: auto;" class="hidden-xs"/>

                        <col style="width: 40px;" class="visible-sm-downward"/>
                        <col style="width: auto;" class="visible-md-upward"/>

                        <col style="width: 80px;" class="visible-xs"/>
                        <col style="width: auto;" class="visible-sm-upward"/>
                    </colgroup>
                    <thead class="screen-reader-only">
                        <tr>
                            <th>Name</th>
                            <th class="hidden-xs">Abteilung</th>
                            <th>E-Mail</th>
                            <th>Telefonnummer</th>
                        </tr>
                    </thead>
                    <tr v-for="employee in telephoneToolbarEmployees" :key="employee.system_id">
                        <td>
                            <person-box-user :id="employee.system_id" class="medium" tooltip hide-contact>
                                <template #name="{username}">
                                    <router-link :to="{name: 'employee-view', params: {id: employee.id}}" :aria-label="username + ' ' + employee.ariaLabel">{{username}}</router-link>
                                </template>

                                <template #image-content v-if="hasAbsencesModule && (isTimeClockMode || !telephoneStatuses[employee.status] || !telephoneStatuses[employee.status].onlyTimeClock)">
                                    <i class="material-icons md-18" :style="{color: employee.badge.color}" :title="employee.badge.title" aria-hidden="true" v-tooltip role="img">{{employee.badge.icon}}</i>
                                </template>

                                <template v-if="hasAbsencesModule && (isTimeClockMode || !telephoneStatuses[employee.status] || !telephoneStatuses[employee.status].onlyTimeClock)">
                                    {{employee.badge.title}}

                                    <small v-if="!filter.hideAbsent && employee.away" class="label small vertical-align-middle">
                                        Abwesend bis {{dateHelper('d.m.Y', employee.away)}}
                                    </small>
                                </template>
                            </person-box-user>
                        </td>

                        <td class="hidden-xs text-overflow" :title="employee.entitiesString" :aria-label="'Mitglied der Abteilung' + (employee.entities.length > 1 ? 'en' : '') + ': ' + employee.entitiesString" tabindex="0" v-tooltip>
                            {{employee.entitiesString}}
                        </td>

                        <td class="text-overflow">
                            <a v-if="employee.email_business" :href="'mailto:' + employee.email_business" :title="employee.email_business"
                                :aria-label="'E-Mail an: ' + employee.email_business" v-tooltip>
                                <i class="material-icons md-20" aria-hidden="true" role="img">email</i>
                                <span class="hidden-sm-down">{{employee.email_business}}</span>
                            </a>
                        </td>

                        <td>
                            <div class="flex">
                                <phone-number v-if="employee.telephone" class="text-overflow" :number="employee.telephone" #default="{number}" :title="employee.telephone"
                                    v-tooltip>
                                    <i class="material-icons md-20" aria-hidden="true" role="img">phone</i>
                                    <span class="hidden-xs">{{number}}</span>
                                </phone-number>
                                <a v-if="employee.callto_sip_address && employee.callto_sip_address_type" class="no-wrap" :class="{'margin-left-5': employee.telephone}"
                                    :href="employee.callto_sip_address_type + employee.callto_sip_address"
                                    :title="employee.callto_sip_address" :aria-label="'Online-Anruf: ' + employee.callto_sip_address" v-tooltip>
                                    <i class="material-icons md-20" aria-hidden="true" role="img">voice_chat</i>
                                    <span :class="employee.telephone ? 'hidden-md-down' : 'hidden-xs'">Online-Anruf</span>
                                </a>
                            </div>
                        </td>
                    </tr>
                </table>
            </template>
        </app-popup>
    </div>
</template>

<script lang="ts">
    import OpenRequestPopup from '@/components/OpenRequestPopup.vue';
    import {telephoneStatuses} from '@/library/modules/employee/employee';
    import websocket from '@/library/websocket';
    import {WEBSOCKET_ACTION_REFETCH_TELEPHONE} from '@/library/websocket/actions';
    import XHRManager from '@/library/xhrManager';
    import {errorHandler} from '@/utils/portuna';
    import {AxiosError} from 'axios';
    import {markRaw} from 'vue';
    import {Component, Vue, Watch} from 'vue-facing-decorator';
    import {Action, Getter, Mutation, State} from 'vuex-facing-decorator';
    import {Filter, orderBy} from '../library/filter';
    import LocalStorage from '../library/localStorage';
    import {getNumericValue} from '../library/math';
    import {addEmployeeChangeListener, removeEmployeeChangeListener} from '../library/modules/employee/employee';
    import {hasTimeClockMode, MODE_MANUAL} from '../library/modules/time-tracker/time-tracker';
    import dateHelper from '../utils/portuna/dateHelper';
    import ActionMenu from './ActionMenu.vue';
    import AppPopup from './AppPopup.vue';
    import AppSearchInput from './AppSearchInput.vue';
    import PersonBoxEmployee from './PersonBoxEmployee.vue';
    import PersonBoxUser from './PersonBoxUser.vue';
    import PhoneNumber from './PhoneNumber.vue';

    @Component({
        name: 'AppToolbarTelephone',
        components: {
            PersonBoxEmployee,
            ActionMenu,
            PhoneNumber,
            PersonBoxUser,
            AppPopup,
            AppSearchInput,
        },
        methods: {
            dateHelper,
        }
    })
    export default class AppToolbarTelephone extends Vue
    {
        readonly $refs: {
            telephonePopup: AppPopup
        };

        @State('telephoneToolbar') telephoneToolbar: TelephoneToolbarState;

        @Action('telephoneToolbar/setOpenState') setOpenState: (open: boolean) => void;

        @Action('telephoneToolbar/refetchTelephoneData') refetchTelephoneList: () => void;

        @Mutation('telephoneToolbar/setOpenRequestCount') setOpenRequestCount: (count) => void;

        @Getter('employeeConfiguration') employeeConfig: EmployeeConfiguration;

        telephoneStatuses = telephoneStatuses;

        isOpen: boolean = false;

        visibility: boolean = true;

        filter: any = {
            search: '',
            status: [],
        };

        get screenMdDown()
        {
            return this.viewPortSize.xs || this.viewPortSize.sm || this.viewPortSize.md;
        }

        get telephoneToolbarEmployees(): EmployeeTelephoneListEntry[]
        {
            let searchFilter = new Filter('status', this.filter.status).and(
                'full_name,telephone,callto_sip_address,email_business,entities,badge.title',
                this.filter.search,
            );

            let employeesFiltered: EmployeeTelephoneListEntry[] = searchFilter.filter(this.telephoneToolbar.employees);
            let employees: EmployeeTelephoneListEntry[] = [];

            for (const filteredEmployee of employeesFiltered)
            {
                let employee = Object.assign({}, filteredEmployee);
                employee.entitiesString = Object.values(employee.entities).join(', ');
                employee.ariaLabel  = 'Status: ' + employee.badge.title + '.';

                if (employee.away)
                {
                    employee.ariaLabel += ' Abwesend bis ' + dateHelper('d.m.Y', employee.away);
                }

                employees.push(employee);
            }

            return orderBy(employees, 'surname');
        }

        get telephoneToolbarList()
        {
            // no absences or not yet configured
            if (this.isTimeTrackerManualWithoutAbsences || !this.employeeConfig.time_tracker_mode)
            {
                return {};
            }

            return this.$store.getters['telephoneToolbar/statusList'];
        }

        get isTimeTrackerManualWithoutAbsences()
        {
            return MODE_MANUAL === this.employeeConfig.time_tracker_mode;
        }

        get isTimeClockMode()
        {
            return hasTimeClockMode();
        }

        get hasAbsencesModule()
        {
            return !!this.employeeConfig.version && this.employeeConfig.allowed_resources.includes('absences') && !this.isTimeTrackerManualWithoutAbsences;
        }

        @Watch('telephoneToolbar.open')
        watchOpenState(open: boolean)
        {
            if (open && !this.isOpen)
            {
                this.openList();
            }
            else if (!open && this.isOpen)
            {
                this.$refs.telephonePopup.close();
            }
        }

        @Watch('isOpen')
        watchOpen(open: boolean)
        {
            this.setOpenState(open);
        }

        created()
        {
            this.visibility = LocalStorage.get('telephoneToolbarVisible', true);
            this.$store.dispatch('telephoneToolbar/fetch');

            addEmployeeChangeListener();

            XHRManager.add(1, 'openRequests', {send: true}, this.updateOpenRequestCount);
            websocket.getManager().then(manager => manager.listen(WEBSOCKET_ACTION_REFETCH_TELEPHONE, this.refetchTelephoneList));
        }

        beforeUnmount()
        {
            XHRManager.remove(1, 'openRequests', this.updateOpenRequestCount);
            removeEmployeeChangeListener();
            websocket.getManager().then(manager => manager.remove(WEBSOCKET_ACTION_REFETCH_TELEPHONE, this.refetchTelephoneList)).catch(errorHandler);
        }

        async openOpenRequestPopup()
        {
            const popup = await this.$store.dispatch('createPopup', {
                title: `Offene Anträge`,
                titleIcon: 'access_time',
                component: markRaw(OpenRequestPopup),
                id: 'open_requests',
            });

            popup.open();
        }

        updateOpenRequestCount(error: Nullable<AxiosError>, data: any)
        {
            if (error)
            {
                return console.error('xhr failed :(', error);
            }

            this.setOpenRequestCount(data.openRequestCount);
        }

        openList(status: Nullable<string | number> = null)
        {
            let statuses   = [];
            let statusKey  = typeof status !== 'string' ? status : parseFloat(status);
            let hideAbsent = true;

            for (let key in telephoneStatuses)
            {
                if (telephoneStatuses.hasOwnProperty(key) && (status === null || telephoneStatuses[key].groupWith === statusKey || key === status))
                {
                    statuses.push(getNumericValue(key));

                    if (telephoneStatuses[key].showAbsent)
                    {
                        hideAbsent = false;
                    }
                }
            }

            this.filter.search     = '';
            this.filter.status     = statuses;
            this.filter.hideAbsent = hideAbsent;

            document.documentElement.classList.add('open-telephone-popup');
            this.$refs.telephonePopup.open();
        }

        closePopup()
        {
            document.documentElement.classList.remove('open-telephone-popup');
        }

        telephoneToolbarToggle()
        {
            this.visibility = !this.visibility;
            LocalStorage.set('telephoneToolbarVisible', this.visibility);
        }
    }
</script>